// AdminSales.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getStock } from '../../api/inventory';
import { getCatalog, getCategory } from '../../api/catalog';
import { createSale } from '../../api/admin/sales';
import './AdminSales.css';
import AutocompleteUser from '../../components/AutocompleteUser';
import { getUserBilling } from '../../api/users';
import { toAbsoluteUrl } from '../../api/admin/docs.api';
import SellerProfileSelect from '../../components/SellerProfileSelect';

function availableOf(p) {
  return Number(p.availableQty ?? Math.max(0, Number(p.stockQty || 0) - Number(p.reservedQty || 0)));
}

function defaultPrices(p) {
  return {
    retail: Number(p.priceRetail) || 0,
    wholesale: Number(p.priceWholesale) || 0,
    mode: 'retail',
  };
}

export default function AdminSales() {
  const [catalog, setCatalog] = useState([]);
  const [subcats, setSubcats] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({ q: '', topCategorySlug: '', subcategoryId: '' });
  const [buyerNote, setBuyerNote] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  /** @type {[{ [id: string]: { productId, title, sku, available, qty } }, Function]} */
  const [cartItems, setCartItems] = useState({});
  const [priceMap, setPriceMap] = useState({});
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [submitting, setSubmitting] = useState(false);

  const [docLinks, setDocLinks] = useState([]);
  const [sellerProfileId, setSellerProfileId] = useState('');

  const [docTypes, setDocTypes] = useState({
    act: false,
    invoice: false,
    order: false,
  });

  const filtersRef = useRef(filters);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);
  const skipDebounceRef = useRef(false);
  const firstFilterEffectRef = useRef(true);

  useEffect(() => { filtersRef.current = filters; }, [filters]);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const f = filtersRef.current;
      const data = await getStock({
        q: f.q,
        topCategorySlug: f.topCategorySlug,
        subcategoryId: f.subcategoryId,
      });

      const arr = Array.isArray(data) ? data : [];
      setProducts(arr);

      // merge prices: keep existing edits, seed missing from stock
      setPriceMap((prev) => {
        const next = { ...prev };
        for (const p of arr) {
          if (!next[p._id]) next[p._id] = defaultPrices(p);
        }
        return next;
      });

      // refresh available/title on cart snapshots still present in result
      setCartItems((prev) => {
        const next = { ...prev };
        let changed = false;
        for (const p of arr) {
          if (next[p._id]) {
            next[p._id] = {
              ...next[p._id],
              title: p.title,
              sku: p.sku || '',
              available: availableOf(p),
            };
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const init = useCallback(async () => {
    const cats = await getCatalog();
    setCatalog(cats || []);
    await loadProducts();
  }, [loadProducts]);

  useEffect(() => { init(); }, [init]);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  // live search / category filter debounce (skip first mount — init already loaded)
  useEffect(() => {
    if (firstFilterEffectRef.current) {
      firstFilterEffectRef.current = false;
      return undefined;
    }
    if (skipDebounceRef.current) {
      skipDebounceRef.current = false;
      return undefined;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadProducts();
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [filters.q, filters.topCategorySlug, filters.subcategoryId, loadProducts]);

  async function handleCategoryChange(e) {
    const slug = e.target.value;
    setFilters((f) => ({ ...f, topCategorySlug: slug, subcategoryId: '' }));
    if (!slug) {
      setSubcats([]);
      return;
    }
    const cat = await getCategory(slug);
    setSubcats(cat?.subcategories || []);
  }

  function handleFilterChange(e) {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  }

  function ensurePrice(id, product) {
    setPriceMap((m) => {
      if (m[id]) return m;
      return { ...m, [id]: product ? defaultPrices(product) : { retail: 0, wholesale: 0, mode: 'retail' } };
    });
  }

  function bumpQty(product, delta) {
    if (!product?._id) return;
    const id = product._id;
    const available = availableOf(product);
    ensurePrice(id, product);

    setCartItems((prev) => {
      const cur = prev[id];
      const curQty = cur?.qty || 0;
      const nextQty = Math.max(0, Math.min(available, curQty + delta));
      const next = { ...prev };
      if (!nextQty) {
        delete next[id];
      } else {
        next[id] = {
          productId: id,
          title: product.title,
          sku: product.sku || '',
          available,
          qty: nextQty,
        };
      }
      return next;
    });
  }

  function setQty(product, value) {
    if (!product?._id) return;
    const id = product._id;
    const available = availableOf(product);
    const v = Math.max(0, Math.min(available, Math.floor(Number(value) || 0)));
    ensurePrice(id, product);

    setCartItems((prev) => {
      const next = { ...prev };
      if (!v) delete next[id];
      else {
        next[id] = {
          productId: id,
          title: product.title,
          sku: product.sku || '',
          available,
          qty: v,
        };
      }
      return next;
    });
  }

  function removeFromCart(id) {
    setCartItems((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  function setPrice(id, field, value) {
    setPriceMap((m) => ({
      ...m,
      [id]: {
        ...(m[id] || { retail: 0, wholesale: 0, mode: 'retail' }),
        [field]: Math.max(0, Number(value) || 0),
      },
    }));
  }

  function setMode(id, mode) {
    setPriceMap((m) => ({
      ...m,
      [id]: { ...(m[id] || { retail: 0, wholesale: 0, mode: 'retail' }), mode },
    }));
  }

  function onSearchKeyDown(e) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const first = products.find((p) => availableOf(p) > 0);
    if (first) bumpQty(first, 1);
  }

  const rowsToSell = useMemo(() => {
    return Object.values(cartItems)
      .filter((c) => c.qty > 0)
      .map((c) => {
        const pr = priceMap[c.productId] || { retail: 0, wholesale: 0, mode: 'retail' };
        const price = pr.mode === 'wholesale' ? pr.wholesale : pr.retail;
        return {
          productId: c.productId,
          title: c.title,
          sku: c.sku || '',
          qty: c.qty,
          price,
          mode: pr.mode,
        };
      });
  }, [cartItems, priceMap]);

  const subtotal = useMemo(() => {
    return rowsToSell.reduce((s, r) => s + r.price * r.qty, 0);
  }, [rowsToSell]);

  async function buildBuyerData() {
    let buyerData = { name: '', inn: '', kpp: '', address: '' };

    if (selectedUser) {
      const billing = await getUserBilling(selectedUser._id);
      buyerData = { ...buyerData, ...(billing || {}) };
    }

    if (!buyerData.name && buyerNote.trim()) {
      buyerData.name = buyerNote.trim();
    }

    if (!buyerData.name) {
      buyerData.name = 'Розничный покупатель';
    }

    return buyerData;
  }

  async function submitSale() {
    if (!rowsToSell.length) return alert('Добавьте хотя бы одну позицию с количеством > 0.');
    setSubmitting(true);

    try {
      const docRequested = Object.values(docTypes).some(Boolean);
      let buyerData = null;
      if (docRequested) {
        buyerData = await buildBuyerData();
      }

      const payload = {
        items: rowsToSell.map((r) => ({
          productId: r.productId,
          qty: r.qty,
          price: r.price,
          appliedPrice: r.price,
          priceMode: r.mode,
          priceType: r.mode,
        })),
        paymentMethod,
        note: selectedUser
          ? (selectedUser.name || selectedUser.phone || selectedUser.email || '')
          : buyerNote || '',
        userId: selectedUser?._id || null,
        buyer: buyerData || undefined,
        docs: docRequested
          ? {
              invoice: Boolean(docTypes.invoice),
              act: Boolean(docTypes.act),
              order: Boolean(docTypes.order),
              sellerProfileId: sellerProfileId || undefined,
            }
          : undefined,
      };

      const saleRes = await createSale(payload);
      const nextLinks = [];
      const docs = saleRes?.docs || {};
      if (docs.invoice?.pdfUrl) nextLinks.push({ type: 'Счёт', url: toAbsoluteUrl(docs.invoice.pdfUrl) });
      if (docs.act?.pdfUrl) nextLinks.push({ type: 'Реализация', url: toAbsoluteUrl(docs.act.pdfUrl) });
      if (docs.order?.pdfUrl) nextLinks.push({ type: 'Заказ клиента', url: toAbsoluteUrl(docs.order.pdfUrl) });
      setDocLinks(nextLinks);

      if (saleRes?.docsError) {
        alert(`Продажа проведена, но документы не созданы: ${saleRes.docsError}`);
      } else {
        alert(saleRes?.orderNumber
          ? `Продажа проведена (заказ ${saleRes.orderNumber})`
          : 'Продажа проведена');
      }

      setCartItems({});
      setBuyerNote('');
      setSelectedUser(null);
      await loadProducts();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || e?.message || 'Ошибка проведения продажи');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="sales-container">
      <h2 className="sales-title admin-page-title">Свободная продажа</h2>

      <div className="sales-filters">
        <input
          ref={searchRef}
          className="fld sales-search"
          name="q"
          placeholder="Поиск товара (Enter — добавить первый)"
          value={filters.q}
          onChange={handleFilterChange}
          onKeyDown={onSearchKeyDown}
        />
        <select
          className="fld"
          name="topCategorySlug"
          value={filters.topCategorySlug}
          onChange={handleCategoryChange}
        >
          <option value="">Все категории</option>
          {catalog.map((c) => (
            <option key={c._id} value={c.slug}>{c.name?.ru || c.slug}</option>
          ))}
        </select>
        <select
          className="fld"
          name="subcategoryId"
          value={filters.subcategoryId}
          onChange={handleFilterChange}
          disabled={!subcats.length}
        >
          <option value="">Все подкатегории</option>
          {subcats.map((s) => (
            <option key={s._id} value={s._id}>{s.name?.ru || s.slug}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            skipDebounceRef.current = true;
            loadProducts();
          }}
        >
          Обновить
        </button>
      </div>

      <div className="sales-workspace">
        <div className="sales-panel sales-panel--buyer">
          <div className="sales-panel__title">Покупатель и документы</div>
          <div className="sales-buyer">
            <AutocompleteUser
              value={selectedUser ? `${selectedUser.name || ''}${selectedUser.phone ? ' · ' + selectedUser.phone : ''}`.trim() : ''}
              onSelect={(u) => setSelectedUser(u)}
              placeholder="Начните вводить имя, телефон или email"
            />
          </div>

          <div className="sales-docs">
            <label className="sales-docs__item">
              <input
                type="checkbox"
                checked={docTypes.act}
                onChange={() => setDocTypes((p) => ({ ...p, act: !p.act }))}
              />
              <span>Реализация</span>
            </label>
            <label className="sales-docs__item">
              <input
                type="checkbox"
                checked={docTypes.invoice}
                onChange={() => setDocTypes((p) => ({ ...p, invoice: !p.invoice }))}
              />
              <span>Счёт</span>
            </label>
            <label className="sales-docs__item">
              <input
                type="checkbox"
                checked={docTypes.order}
                onChange={() => setDocTypes((p) => ({ ...p, order: !p.order }))}
              />
              <span>Заказ клиента</span>
            </label>
          </div>

          {(docTypes.act || docTypes.invoice || docTypes.order) && (
            <div className="sales-seller">
              <SellerProfileSelect value={sellerProfileId} onChange={setSellerProfileId} />
            </div>
          )}

          <input
            className="fld"
            placeholder="Имя / комментарий покупателя (если не выбран пользователь)"
            value={buyerNote}
            onChange={(e) => setBuyerNote(e.target.value)}
          />
        </div>

        <div className="sales-panel sales-panel--summary">
          <div className="sales-panel__title">Итог</div>

          <div className="sales-summary__positions">
            <div className="sales-summary__row">
              <span>Позиции</span>
              <b>{rowsToSell.length}</b>
            </div>
            {rowsToSell.length === 0 ? (
              <div className="sales-summary__empty">Ничего не выбрано</div>
            ) : (
              <ul className="sales-summary__list">
                {rowsToSell.map((r) => (
                  <li key={r.productId} className="sales-summary__item">
                    <div className="sales-summary__item-main">
                      <span className="sales-summary__item-title">{r.title}</span>
                      <span className="sales-summary__item-meta">
                        {r.qty} × {r.price.toLocaleString('ru-RU')} ₽
                      </span>
                    </div>
                    <b className="sales-summary__item-sum">
                      {(r.qty * r.price).toLocaleString('ru-RU')} ₽
                    </b>
                    <button
                      type="button"
                      className="sales-summary__remove"
                      onClick={() => removeFromCart(r.productId)}
                      title="Убрать"
                      aria-label="Убрать"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="sales-summary__row sales-summary__row--total">
            <span>Сумма</span>
            <b>{subtotal.toLocaleString('ru-RU')} ₽</b>
          </div>

          <label className="sales-pay">
            <span>Оплата</span>
            <select className="fld" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
              <option value="cash">Наличные</option>
              <option value="bank">Безнал</option>
            </select>
          </label>
          <button
            type="button"
            className="sales-submit"
            disabled={submitting || !rowsToSell.length}
            onClick={submitSale}
          >
            {submitting ? 'Провожу…' : 'Провести продажу'}
          </button>
        </div>
      </div>

      {docLinks.length > 0 && (
        <div className="sales-docs-result" aria-live="polite">
          <div className="sales-docs-result__head">
            <span>Документы готовы</span>
            <button type="button" className="sales-docs-result__clear" onClick={() => setDocLinks([])}>
              Скрыть
            </button>
          </div>
          <div className="sales-docs-result__links">
            {docLinks.map((link) => (
              <a
                key={link.url}
                className="sales-doc-btn"
                href={link.url}
                target="_blank"
                rel="noreferrer"
              >
                <span className="sales-doc-btn__icon" aria-hidden>⬇</span>
                <span>Скачать {link.type}</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <p>Загрузка…</p>
      ) : (
        <table className="sales-table">
          <thead>
            <tr>
              <th style={{ width: '34%' }}>Товар</th>
              <th>Склад</th>
              <th>Резерв</th>
              <th>Свободно</th>
              <th>Цена (розн.)</th>
              <th>Цена (опт)</th>
              <th>Применять</th>
              <th style={{ width: 160 }}>Кол-во</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => {
              const pr = priceMap[p._id] || defaultPrices(p);
              const available = availableOf(p);
              const qty = cartItems[p._id]?.qty || 0;
              const selected = qty > 0;
              return (
                <tr key={p._id} className={selected ? 'is-selected' : undefined}>
                  <td>
                    <button
                      type="button"
                      className="sales-product-name"
                      onClick={() => bumpQty(p, 1)}
                      disabled={available <= 0 || qty >= available}
                      title={available > 0 ? 'Добавить +1' : 'Нет в наличии'}
                    >
                      {p.title}
                    </button>
                  </td>
                  <td>{p.stockQty}</td>
                  <td>{p.reservedQty || 0}</td>
                  <td><b>{available}</b></td>
                  <td>
                    <input
                      className="fld"
                      type="number"
                      min="0"
                      step="1"
                      value={pr.retail}
                      onChange={(e) => setPrice(p._id, 'retail', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="fld"
                      type="number"
                      min="0"
                      step="1"
                      value={pr.wholesale}
                      onChange={(e) => setPrice(p._id, 'wholesale', e.target.value)}
                    />
                  </td>
                  <td className="price-mode">
                    <label className="radio">
                      <input
                        type="radio"
                        name={`mode-${p._id}`}
                        checked={pr.mode === 'retail'}
                        onChange={() => setMode(p._id, 'retail')}
                      />
                      <span>Розн.</span>
                    </label>
                    <label className="radio">
                      <input
                        type="radio"
                        name={`mode-${p._id}`}
                        checked={pr.mode === 'wholesale'}
                        onChange={() => setMode(p._id, 'wholesale')}
                      />
                      <span>Опт</span>
                    </label>
                  </td>
                  <td>
                    <div className="qty-stepper">
                      <button
                        type="button"
                        className="qty-stepper__btn"
                        onClick={() => bumpQty(p, -1)}
                        disabled={qty <= 0}
                        aria-label="Минус"
                      >
                        −
                      </button>
                      <input
                        className="fld qty"
                        type="number"
                        min="0"
                        max={available}
                        step="1"
                        value={qty || ''}
                        onChange={(e) => setQty(p, e.target.value)}
                        placeholder="0"
                      />
                      <button
                        type="button"
                        className="qty-stepper__btn"
                        onClick={() => bumpQty(p, 1)}
                        disabled={available <= 0 || qty >= available}
                        aria-label="Плюс"
                      >
                        +
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
